/** @file Maps a key code on a Tree to one action (focus, toggle, select, rename) from the visible rows. */
import type { TreeRow } from './flattenTree';

/** What the Tree should do for a key; 'none' means the key is not handled. */
export type TreeKeyAction =
  | { kind: 'focus'; id: string }
  | { kind: 'toggle'; id: string; expanded: boolean }
  | { kind: 'select'; id: string }
  | { kind: 'rename'; id: string }
  | { kind: 'none' };

const NONE: TreeKeyAction = { kind: 'none' };
const focusRow = (row: TreeRow | undefined): TreeKeyAction => (row ? { kind: 'focus', id: row.id } : NONE);

type Handler = (rows: readonly TreeRow[], index: number, row: TreeRow) => TreeKeyAction;

const HANDLERS = new Map<string, Handler>([
  ['ArrowDown', (rows, index) => focusRow(rows[Math.min(index + 1, rows.length - 1)])],
  ['ArrowUp', (rows, index) => focusRow(rows[Math.max(index - 1, 0)])],
  ['Home', (rows) => focusRow(rows[0])],
  ['End', (rows) => focusRow(rows.at(-1))],
  ['ArrowRight', (rows, index, row) => (!row.hasChildren ? NONE : row.expanded ? focusRow(rows[index + 1]) : { kind: 'toggle', id: row.id, expanded: true })],
  ['ArrowLeft', (rows, index, row) => (row.hasChildren && row.expanded ? { kind: 'toggle', id: row.id, expanded: false } : focusRow(rows.find((r) => r.id === row.parentId)))],
  ['Enter', (rows, index, row) => ({ kind: 'select', id: row.id })],
  ['NumpadEnter', (rows, index, row) => ({ kind: 'select', id: row.id })],
  ['Space', (rows, index, row) => ({ kind: 'select', id: row.id })],
  ['F2', (rows, index, row) => ({ kind: 'rename', id: row.id })],
]);

/** Keys that start at the first / last row when no row is active yet. */
const FROM_START = new Set(['ArrowDown', 'Home']);
const FROM_END = new Set(['ArrowUp', 'End']);

/** The action for key `code` (a `KeyboardEvent.code`) when `activeId` is the focused row (null = none yet). */
export function treeKeyAction(rows: readonly TreeRow[], activeId: string | null, code: string): TreeKeyAction {
  const index = rows.findIndex((r) => r.id === activeId);
  const row = rows[index];
  if (!row) return FROM_START.has(code) ? focusRow(rows[0]) : FROM_END.has(code) ? focusRow(rows.at(-1)) : NONE;
  return HANDLERS.get(code)?.(rows, index, row) ?? NONE;
}
